/**
 * Data Manager — pure helpers and the upload wizard's state machine (M6 §5).
 *
 * Everything the wizard decides lives here, as a reducer over a plain object: which step is
 * showing, whether Next is allowed, what the metadata post looks like. The components read
 * it and render; nothing in this file touches the DOM, the network or React, so the four
 * steps' rules can be asserted in `manager.test.ts` without mounting anything.
 */
import type {
  DatasetDataType,
  DatasetPatch,
  DatasetRecord,
  IndicatorSubmissionDiff,
  IndicatorSubmissionRow,
  UploadCheck,
  UploadChecksMeta,
  UploadMetadata,
  UploadPreviewRow,
  UploadRow,
} from '../../lib/api';
import type { UploadProgress } from '../../lib/upload';

/** §5 dropzone — "GeoTIFF, Shapefile (zip), GeoPackage, GeoJSON, CSV, XLSX — up to 10 GB". */
export const ACCEPTED_EXTENSIONS = [
  '.tif',
  '.tiff',
  '.zip',
  '.gpkg',
  '.geojson',
  '.json',
  '.csv',
  '.xlsx',
] as const;

/** §1's cap, restated on the client so an oversize file is refused before a byte is sent. */
export const MAX_UPLOAD_BYTES = 10 * 1024 ** 3;

/** §5 licence select. Values are what the catalogue stores, not translated labels. */
export const LICENCES = ['CC-BY-4.0', 'CC-BY-SA-4.0', 'ODbL', 'Programme data', 'Other'] as const;

/** §5 update-cadence select. */
export const CADENCES = ['one-off', 'monthly', 'annual', 'irregular'] as const;

/** §5 classification radio cards, in the order they are shown. */
export const CLASSIFICATIONS = ['open', 'sensitive', 'restricted'] as const;

export type Classification = (typeof CLASSIFICATIONS)[number];

/** The extension of `name`, lower case and with its dot; `''` when there is none. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

export const isAcceptedFile = (name: string): boolean =>
  (ACCEPTED_EXTENSIONS as readonly string[]).includes(extensionOf(name));

/**
 * The `kind_hint` the create call carries.
 *
 * Only a hint: the server's validator decides the real `kind` once it can open the file.
 * `.json` is deliberately absent — it is `.geojson`'s careless cousin and could be either —
 * and so is `.zip`, which the server has to open to know what is inside.
 */
export function kindHintFor(name: string): 'spatial' | 'tabular' | null {
  const ext = extensionOf(name);
  if (ext === '.tif' || ext === '.tiff' || ext === '.gpkg' || ext === '.geojson') return 'spatial';
  if (ext === '.csv' || ext === '.xlsx') return 'tabular';
  return null;
}

/**
 * A first guess at the title, from the filename: `soil_organic_carbon.tif` → "soil organic
 * carbon". Only ever used to *seed* an empty field — the reader's own typing always wins.
 */
export function titleFromFilename(name: string): string {
  const stem = name.slice(0, name.length - extensionOf(name).length);
  return stem.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/* ------------------------------------------------------------------ wizard state */

export type WizardStep = 1 | 2 | 3 | 4;

/**
 * Where the transfer is.
 *
 * `checking` is not used by the reducer's own transitions — the server owns it — but the
 * step-4 panel prints it while the validator runs, so it is part of the vocabulary.
 */
export type UploadPhase = 'idle' | 'uploading' | 'paused' | 'ready' | 'error';

export interface MetadataForm {
  title: string;
  domain: string;
  source: string;
  licence: string;
  cadence: string;
  abstract: string;
  lineage: string;
  contact: string;
  /**
   * The provenance four (client, 2026-09-22): *"we should capture the data source as well,
   * how it was captured and accuracy information; for rasters, it is good to also capture
   * resolution information."*
   *
   * Wire names rather than camelCase, unlike `websiteVisible` next door, because these four
   * go out under exactly these keys and come back under them — one spelling is one fewer
   * mapping to keep in step with `uploads.METADATA_FIELDS`. All four are optional: none is
   * in `REQUIRED_FIELDS`, so a reader who does not know the accuracy can still publish.
   */
  source_detail: string;
  capture_method: string;
  accuracy: string;
  /** Prefilled from `checks_meta.resolution` for a raster, typed by hand for anything else. */
  resolution: string;
  classification: Classification | '';
  /** §4 — whether the dataset shows on the public catalogue once it is published. */
  websiteVisible: boolean;
  /** §2 — a raster whose values are classes, not measurements. */
  isCategorical: boolean;
  /**
   * Who is submitting (client, 2026-09-18).
   *
   * Part of the *form* rather than of the page around it because the server wants both on
   * `POST /uploads` — before a byte is sent — and wants them again on every metadata save.
   * One place to read them from is what keeps those two calls from disagreeing.
   */
  submitterName: string;
  submitterEmail: string;
}

/** The starred fields of §5 step 2, plus step 3's classification. */
export const REQUIRED_FIELDS = [
  'title',
  'domain',
  'source',
  'licence',
  'cadence',
  'abstract',
] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];

export interface WizardState {
  step: WizardStep;
  /** The picked file. Kept so pause/resume can hand the same bytes back to `resumeUpload`. */
  file: File | null;
  uploadId: number | null;
  phase: UploadPhase;
  progress: UploadProgress;
  /** The last `UploadRow` seen — the source of the detected CRS/extent and the checks. */
  row: UploadRow | null;
  /** A message *key* under `manager.err.*`, never prose: this module does not translate. */
  error: string | null;
  form: MetadataForm;
  /** "This is a new version of…" — the dataset id, or null for a brand new dataset. */
  versionOf: string | null;
  /**
   * The measured resolution already offered to the form, if any.
   *
   * It lives in the wizard state rather than in a ref inside step 2 because step 2 is
   * unmounted the moment the reader moves to step 3: a ref would forget, and a field the
   * reader had deliberately emptied would refill on the way back. Only a new file clears it,
   * which is exactly when a new measurement deserves to be offered again.
   */
  resolutionOffered: string | null;
}

const emptyForm = (): MetadataForm => ({
  title: '',
  domain: '',
  source: '',
  licence: '',
  cadence: '',
  abstract: '',
  lineage: '',
  contact: '',
  source_detail: '',
  capture_method: '',
  accuracy: '',
  resolution: '',
  classification: 'open',
  websiteVisible: true,
  isCategorical: false,
  submitterName: '',
  submitterEmail: '',
});

/**
 * A fresh wizard, optionally aimed at a dataset from the start.
 *
 * `versionOf` is a parameter rather than a later dispatch because the Datasets tab's "New
 * version" hands the wizard a target *before* the reader picks a file — and the target is
 * part of `POST /uploads`, so it has to be in the state the first render reads.
 */
export const initialWizardState = (versionOf: string | null = null): WizardState => ({
  step: 1,
  file: null,
  uploadId: null,
  phase: 'idle',
  progress: { sent: 0, total: 0, parts: 0, ratio: 0 },
  row: null,
  error: null,
  form: emptyForm(),
  versionOf,
  resolutionOffered: null,
});

export type WizardAction =
  | { type: 'file'; file: File }
  | { type: 'session'; id: number }
  | { type: 'progress'; progress: UploadProgress }
  | { type: 'finished'; row: UploadRow | null }
  | { type: 'row'; row: UploadRow }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'cancel' }
  | { type: 'error'; message: string }
  | { type: 'form'; patch: Partial<MetadataForm> }
  | { type: 'version'; datasetId: string | null }
  | { type: 'resolutionOffered'; value: string }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'goto'; step: WizardStep };

/**
 * Is this an address the portal is willing to send a decision to?
 *
 * One `@`, something either side of it, a dot in the domain and no whitespace — the shape a
 * typo fails and a real address passes. Deliberately not the RFC: a stricter pattern here
 * would refuse addresses the mail server accepts, and the server validates too.
 */
export const isEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(value.trim());

/** Both submitter fields answered, the address plausibly an address. */
export const submitterOk = (form: Pick<MetadataForm, 'submitterName' | 'submitterEmail'>): boolean =>
  form.submitterName.trim() !== '' && isEmail(form.submitterEmail);

/**
 * Can the reader leave `step`?
 *
 * Step 1 waits for the transfer to finish, because every later step edits an upload that
 * has to exist server-side, and for the submitter block, which the server requires on the
 * session itself; step 2 wants the starred fields; step 3 wants a classification
 * (pre-set to `open`, so it is satisfied unless the reader clears it); step 4 is the submit
 * step and is always ready.
 */
export function canProceed(step: WizardStep, state: WizardState): boolean {
  if (step === 1) return state.uploadId !== null && state.phase === 'ready' && submitterOk(state.form);
  if (step === 2) return REQUIRED_FIELDS.every((key) => state.form[key].trim() !== '');
  if (step === 3) return state.form.classification !== '';
  return true;
}

/**
 * The body of `POST /uploads/{id}/metadata` — trimmed, under the **server's** key names.
 *
 * `uploads.METADATA_FIELDS` is an allowlist: a key it does not know is dropped silently, so
 * the form's `source`/`isCategorical` have to arrive as `source_org` and `categorical` or
 * they are simply lost. `dataset_id` is the new-version target and is written to
 * `Upload.dataset`.
 */
export function metadataPayload(state: WizardState): UploadMetadata {
  const f = state.form;
  return {
    title: f.title.trim(),
    domain: f.domain,
    source_org: f.source.trim(),
    licence: f.licence,
    cadence: f.cadence,
    abstract: f.abstract.trim(),
    lineage: f.lineage.trim(),
    contact: f.contact.trim(),
    source_detail: f.source_detail.trim(),
    capture_method: f.capture_method.trim(),
    accuracy: f.accuracy.trim(),
    resolution: f.resolution.trim(),
    classification: f.classification || 'open',
    website_visible: f.websiteVisible,
    categorical: f.isCategorical,
    submitter_name: f.submitterName.trim(),
    submitter_email: f.submitterEmail.trim(),
    dataset_id: state.versionOf,
  };
}

const nextStep = (step: WizardStep): WizardStep => (step < 4 ? ((step + 1) as WizardStep) : step);
const prevStep = (step: WizardStep): WizardStep => (step > 1 ? ((step - 1) as WizardStep) : step);

/**
 * A published dataset's record as a wizard form patch — the Replace flow's prefill.
 *
 * Only the fields the record actually carries: the submitter block, the categorical flag and
 * anything the record leaves null stay as they are, so a record with no licence does not
 * blank a licence the form already holds.
 */
/** The record-only fields: the list row does not carry them, so a form seeded from a row holds
 *  `null` for each until `GET /datasets/{id}` answers. */
export const RECORD_ONLY_FIELDS = [
  'licence',
  'cadence',
  'lineage',
  'contact',
  'source_detail',
  'capture_method',
  'accuracy',
  'resolution',
] as const;

/**
 * Fold a record into a form the reader may already be editing.
 *
 * Untouched: the record replaces the form wholesale. Touched: only the record-only fields
 * that are *still* null are filled — the reader's edits stand, and a licence they picked
 * before the record arrived is not overwritten by the old one. Without this, an edit typed
 * before the record landed froze those four at null, and the save then blanked them.
 */
export function foldRecord(form: DatasetPatch, rec: DatasetRecord, touched: boolean): DatasetPatch {
  const full: DatasetPatch = {
    title: rec.title,
    abstract: rec.abstract,
    domain: rec.domain,
    source_org: rec.source_org,
    licence: rec.licence,
    cadence: rec.cadence,
    lineage: rec.lineage,
    contact: rec.contact,
    source_detail: rec.source_detail,
    capture_method: rec.capture_method,
    accuracy: rec.accuracy,
    resolution: rec.resolution,
    classification: rec.classification,
    website_visible: rec.website_visible,
  };
  if (!touched) return full;
  const out = { ...form };
  for (const key of RECORD_ONLY_FIELDS) if (out[key] === null || out[key] === undefined) out[key] = full[key];
  return out;
}

/**
 * The PATCH body. When the record never arrived, the record-only fields are unknown rather
 * than empty, and a `null` on the wire means "clear it" — so they are left out.
 */
export function patchBody(form: DatasetPatch, recordLoaded: boolean): Partial<DatasetPatch> {
  if (recordLoaded) return form;
  const out: Partial<DatasetPatch> = { ...form };
  for (const key of RECORD_ONLY_FIELDS) delete out[key];
  return out;
}

export function prefillFromRecord(rec: DatasetRecord): Partial<MetadataForm> {
  const patch: Partial<MetadataForm> = {
    title: rec.title,
    domain: rec.domain ?? '',
    source: rec.source_org ?? '',
    abstract: rec.abstract ?? '',
    classification: rec.classification,
    websiteVisible: rec.website_visible,
  };
  if (rec.licence) patch.licence = rec.licence;
  if (rec.cadence) patch.cadence = rec.cadence;
  if (rec.lineage) patch.lineage = rec.lineage;
  if (rec.contact) patch.contact = rec.contact;
  if (rec.source_detail) patch.source_detail = rec.source_detail;
  if (rec.capture_method) patch.capture_method = rec.capture_method;
  if (rec.accuracy) patch.accuracy = rec.accuracy;
  if (rec.resolution) patch.resolution = rec.resolution;
  return patch;
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'file': {
      // Both refusals are states, not exceptions: the dropzone prints the message and stays
      // open, which is what a reader who dropped the wrong thing needs.
      if (!isAcceptedFile(action.file.name)) {
        return { ...state, file: null, error: 'unsupported' };
      }
      if (action.file.size > MAX_UPLOAD_BYTES) {
        return { ...state, file: null, error: 'tooBig' };
      }
      return {
        ...state,
        file: action.file,
        uploadId: null,
        row: null,
        phase: 'uploading',
        error: null,
        resolutionOffered: null,
        progress: { sent: 0, total: action.file.size, parts: 0, ratio: 0 },
      };
    }
    case 'session':
      return { ...state, uploadId: action.id };
    case 'progress':
      return { ...state, progress: action.progress };
    case 'finished': {
      const seeded =
        state.form.title.trim() === '' && state.file
          ? { ...state.form, title: titleFromFilename(state.file.name) }
          : state.form;
      return {
        ...state,
        phase: 'ready',
        error: null,
        row: action.row ?? state.row,
        form: seeded,
        progress: { ...state.progress, ratio: 1, sent: state.progress.total },
      };
    }
    case 'row':
      return { ...state, row: action.row };
    case 'pause':
      return state.phase === 'uploading' ? { ...state, phase: 'paused' } : state;
    case 'resume':
      // Retry is the same control as Resume: a transfer that failed mid-part carries on from
      // the parts the server kept, exactly as a paused one does.
      return state.phase === 'paused' || state.phase === 'error'
        ? { ...state, phase: 'uploading', error: null }
        : state;
    case 'cancel':
      // The preset target survives a cancel: the reader came here from "New version of X"
      // and dropping the wrong file does not change which dataset they meant.
      return initialWizardState(state.versionOf);
    case 'error':
      return { ...state, phase: 'error', error: action.message };
    case 'form':
      return { ...state, form: { ...state.form, ...action.patch } };
    case 'version':
      return { ...state, versionOf: action.datasetId };
    case 'resolutionOffered':
      return { ...state, resolutionOffered: action.value };
    case 'next':
      return canProceed(state.step, state) ? { ...state, step: nextStep(state.step) } : state;
    case 'back':
      return { ...state, step: prevStep(state.step) };
    case 'goto':
      return { ...state, step: action.step };
    default:
      return state;
  }
}

/* ------------------------------------------------- the "new version of…" target list */

/**
 * The catalogue `data_type` a file of this name would publish as.
 *
 * Wider than `kindHintFor`, and for a different question: that one hints at the *family* the
 * server should expect, this one names the row a new version would have to replace. `.zip`
 * and `.json` are vector here because a new version of a vector dataset is the only thing
 * either can sensibly be — a zipped Shapefile and a GeoJSON — whereas as a *hint* they are
 * ambiguous enough that the server is better off opening the file.
 */
export function dataTypeForFile(name: string): DatasetDataType | null {
  const ext = extensionOf(name);
  if (ext === '.tif' || ext === '.tiff') return 'raster';
  if (ext === '.zip' || ext === '.gpkg' || ext === '.geojson' || ext === '.json') return 'vector';
  if (ext === '.csv' || ext === '.xlsx') return 'tabular';
  return null;
}

/**
 * Which datasets this upload may be offered as a new version of.
 *
 * Two filters, both of which the server also enforces (an untargetable dataset is refused on
 * create, not silently accepted):
 *
 * * **`deletable`** — the manager's own published datasets. The rest are pipeline-owned
 *   (harvested, or seeded by a migration): a new version of one of those would be overwritten
 *   by the next harvest, so offering them is offering something that cannot work.
 * * **matching `data_type`** — once a file is picked. A GeoTIFF cannot become a new version
 *   of a CSV: the publish path is a different one and the layer behind the dataset is a
 *   different kind. Before a file is picked nothing is known, so nothing is filtered.
 */
export function versionTargets<T extends { data_type: DatasetDataType; deletable?: boolean }>(
  datasets: readonly T[],
  fileName: string | null,
): T[] {
  const wanted = fileName ? dataTypeForFile(fileName) : null;
  return datasets.filter(
    (d) => d.deletable === true && (wanted === null || d.data_type === wanted),
  );
}

/* ------------------------------------------------------------------ detected facts */

export interface DetectedFact {
  /** A key under `manager.detected.*`. */
  key: string;
  value: string;
}

/**
 * The read-only "what the checks found" rows of step 2 — only the keys the validator
 * actually filled, so an upload whose checks have not run yet shows nothing rather than a
 * table of dashes.
 */
export function detectedFacts(meta: UploadChecksMeta | null | undefined): DetectedFact[] {
  if (!meta) return [];
  const out: DetectedFact[] = [];
  if (meta.crs) out.push({ key: 'crs', value: meta.crs });
  if (meta.extent && meta.extent.length === 4) {
    out.push({ key: 'extent', value: meta.extent.map((n) => n.toFixed(3)).join(', ') });
  }
  if (typeof meta.bands === 'number') out.push({ key: 'bands', value: String(meta.bands) });
  if (meta.dtype) out.push({ key: 'dtype', value: meta.dtype });
  // The raster's pixel size, printed by the validator (client, 2026-09-22). It is also what
  // step 2's Resolution field is prefilled from, so the reader can see the number they are
  // being offered next to the rest of what the file said about itself.
  if (meta.resolution) out.push({ key: 'resolution', value: meta.resolution });
  if (meta.geometry_type) out.push({ key: 'geometry', value: meta.geometry_type });
  if (typeof meta.feature_count === 'number') {
    out.push({ key: 'features', value: meta.feature_count.toLocaleString() });
  }
  if (meta.columns?.length) out.push({ key: 'columns', value: String(meta.columns.length) });
  const rows = rowCount(meta);
  if (rows !== null) out.push({ key: 'rows', value: rows.toLocaleString() });
  return out;
}

/**
 * How many data rows the tabular upload has.
 *
 * `row_count` first and the sample's length only as a fallback: `checks_meta.rows` is the
 * **first 100 rows**, so counting it would tell the reader a 40 000-row CSV has 100 — which
 * is exactly what the preview's own caption says it is showing, and not the same statement.
 */
export function rowCount(meta: UploadChecksMeta | null | undefined): number | null {
  if (!meta) return null;
  if (typeof meta.row_count === 'number') return meta.row_count;
  if (Array.isArray(meta.rows)) return meta.rows.length;
  return null;
}

/** `3 / 5` for the review step's check summary. */
export const checkTally = (row: UploadRow | null): { passed: number; total: number } => ({
  passed: row?.checks?.filter((c) => c.passed).length ?? 0,
  total: row?.checks?.length ?? 0,
});

/**
 * A byte count as a person reads it — `1.4 GB`, `812 kB`.
 *
 * Decimal units, not binary: the dropzone promises "up to 10 GB" and the server's cap is
 * `10 * 1024 ** 3`, so printing GiB here would have the card say 9.3 GB for a file the
 * wizard had just accepted.
 */
export function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const value = bytes / 1000 ** i;
  return `${value.toFixed(i === 0 ? 0 : value < 10 ? 1 : 0)} ${units[i]}`;
}

/* ------------------------------------------------------------------ review queue (§5) */

/** §5: the queue refetches while anything is still being checked. */
export const QUEUE_POLL_MS = 10_000;

/**
 * §5: the open review dialog refetches its own row while that row is still being checked.
 *
 * Faster than the queue's 10 s because it is one row and someone is looking straight at it,
 * waiting for Approve to come alive.
 */
export const REVIEW_POLL_MS = 5_000;

/** The queue's filter, `all` meaning "send no `status` at all". */
export const QUEUE_FILTERS = ['all', 'pending', 'failed', 'review', 'approved', 'rejected'] as const;

export type QueueFilter = (typeof QUEUE_FILTERS)[number];

/**
 * Is anything still moving?
 *
 * `pending` covers both halves of the wait — bytes still arriving and the Celery validator
 * still running — and it is the only status that changes on its own. A queue of decided rows
 * polls nothing: the reviewer's own decisions invalidate the list.
 */
export const hasPending = (rows: readonly UploadRow[]): boolean =>
  rows.some((r) => r.status === 'pending');

/** True for an §2 indicator submission — the one upload kind that is not a file of data. */
export const isSubmission = (row: UploadRow): boolean => row.kind === 'indicator_values';

/**
 * What the queue and the modal call an upload: its metadata title, else its filename.
 *
 * A **submission** is titled `d2 · 2026` instead (§2). It has no dataset title — the wizard
 * that collects one never runs for it — and `d2_2026.csv` is a filename the browser made up
 * from the grid, so calling the row by its indicator and period is the only version of this
 * line that tells a reviewer what they are about to open. Metadata first, the validator's
 * own reading second, the filename last: a submission whose metadata post has not landed yet
 * still has to be called something.
 */
export function uploadTitle(row: UploadRow): string {
  const title = typeof row.metadata?.title === 'string' ? row.metadata.title.trim() : '';
  if (isSubmission(row)) {
    const indicator = String(row.metadata?.indicator_id ?? row.checks_meta?.indicator_id ?? '').trim();
    const period = String(row.metadata?.period ?? row.checks_meta?.period ?? '').trim();
    const stamp = [indicator, period].filter(Boolean).join(' · ');
    if (stamp) return stamp;
  }
  return title || row.filename;
}

/* ---------------------------------------------- §2 indicator submissions */

/** Which bucket one submitted row falls in — the review diff's row class. */
export type DiffState = 'add' | 'change' | 'same' | 'blank';

/**
 * A blank value is **not** a cleared one: approving skips it and leaves the stored row
 * alone (§2), so it is its own bucket rather than a change to nothing.
 */
export function diffState(row: IndicatorSubmissionRow): DiffState {
  if (row.value === null || row.value === undefined) return 'blank';
  if (row.current === null || row.current === undefined) return 'add';
  return row.value === row.current ? 'same' : 'change';
}

/**
 * The keyed rows of an `indicator_values` submission, and nothing else.
 *
 * `checks_meta.rows` is one field carrying two shapes — a tabular upload's positional cells
 * and a submission's keyed rows — so this is the narrowing half of `previewTable`'s: arrays
 * are somebody else's rows, objects are these.
 */
export function submissionRows(
  meta: UploadChecksMeta | null | undefined,
): IndicatorSubmissionRow[] {
  if (!meta || !Array.isArray(meta.rows)) return [];
  return (meta.rows as unknown[]).filter(
    (r): r is IndicatorSubmissionRow => !Array.isArray(r) && typeof r === 'object' && r !== null,
  );
}

/**
 * The four counts under the diff — **the server's own tally when it sent one**.
 *
 * `rows` is capped at `MAX_META_ROWS` and says so with `rows_truncated`, so counting the
 * rows on screen would under-report a large submission in the one line that is supposed to
 * say how big it is. Counting them here is the fallback for a payload without the field.
 */
export function submissionDiff(meta: UploadChecksMeta | null | undefined): IndicatorSubmissionDiff {
  if (meta?.diff) return meta.diff;
  const counts: IndicatorSubmissionDiff = { added: 0, changed: 0, unchanged: 0, blank: 0 };
  for (const row of submissionRows(meta)) {
    const state = diffState(row);
    if (state === 'add') counts.added += 1;
    else if (state === 'change') counts.changed += 1;
    else if (state === 'same') counts.unchanged += 1;
    else counts.blank += 1;
  }
  return counts;
}

/**
 * Has every **blocking** check passed?
 *
 * Advisory failures are deliberately not counted: `single_band` and `nodata_detected` are
 * notes to the reviewer, and letting one of them grey out Approve would make the reviewer's
 * only route a Return the submitter cannot act on. An upload whose checks have not run at all
 * is not approvable either — there is nothing to have passed.
 */
export const blockingOk = (row: UploadRow): boolean =>
  row.checks.length > 0 && row.checks.every((c) => c.passed || !c.blocking);

/** `ok` passed · `warn` advisory and not passed · `bad` blocking and failed. */
export const checkState = (check: UploadCheck): 'ok' | 'warn' | 'bad' =>
  check.passed ? 'ok' : check.blocking ? 'bad' : 'warn';

/** The glyph each of those three states carries in the checks list (§5). */
export const CHECK_GLYPH: Record<'ok' | 'warn' | 'bad', string> = {
  ok: '✓',
  warn: '○',
  bad: '✗',
};

/**
 * Which preview the review modal can show, or `null` for "nothing to show yet".
 *
 * Driven by what the validator actually produced rather than by the format alone: a GeoTIFF
 * whose COG has not been written has no `preview_tiles`, and a map with a basemap and no data
 * on it reads as "the upload is empty" rather than "the checks are still running".
 */
/** Vector previews are drawn in the programme's own green, at a weight a 240 px box can show. */
export const VECTOR_STYLE = { color: '#3F7A38', weight: 1.4, fillColor: '#3F7A38', fillOpacity: 0.18 };

export function previewKind(row: UploadRow): 'raster' | 'vector' | 'table' | null {
  // A submission's CSV is a list of unit ids and numbers, and the dialog shows the *diff*
  // those numbers make instead (§2). A preview of the raw file would be the same rows
  // twice, one of the two readings without the values already on file beside them.
  if (isSubmission(row)) return null;
  if (row.checks_meta?.preview_tiles) return 'raster';
  if (row.kind === 'tabular' || row.format === 'csv' || row.format === 'xlsx') {
    return previewTable(row.checks_meta) ? 'table' : null;
  }
  if (row.format === 'shapefile' || row.format === 'gpkg' || row.format === 'geojson') {
    return 'vector';
  }
  return null;
}

/**
 * `[w, s, e, n]` as Leaflet's `[[s, w], [n, e]]`, or `null` when there is no usable extent.
 *
 * The check is not decoration: a validator that could not read a CRS reports `[0,0,0,0]`, and
 * fitting a map to a degenerate box zooms it to the maximum over the Gulf of Guinea.
 */
export function leafletBounds(
  extent: readonly number[] | undefined,
): [[number, number], [number, number]] | null {
  if (!extent || extent.length !== 4 || extent.some((n) => !Number.isFinite(n))) return null;
  const [w, s, e, n] = extent as [number, number, number, number];
  if (e <= w || n <= s) return null;
  return [
    [s, w],
    [n, e],
  ];
}

export interface PreviewTable {
  columns: string[];
  /** Already stringified — the table prints what the validator read, not a re-formatting. */
  rows: string[][];
}

/**
 * The §2 tabular sample as a table.
 *
 * Rows are positional and already padded to the header server-side, so they are read *by
 * index through* `columns` — a short row prints trailing blanks rather than shifting a whole
 * column one place left. A sample with no `columns` beside it (a header the validator could
 * not use) is numbered `1…n` rather than dropped: the reviewer can still see the data they
 * are being asked about.
 */
export function previewTable(meta: UploadChecksMeta | null | undefined): PreviewTable | null {
  if (!meta || !Array.isArray(meta.rows) || meta.rows.length === 0) return null;
  // `checks_meta.rows` is positional for a tabular upload and keyed for an
  // `indicator_values` submission (§D3), which has its own review screen. Only the
  // positional ones are a preview table; a keyed row here is simply not this function's.
  const sample = (meta.rows as unknown[]).filter((r): r is UploadPreviewRow => Array.isArray(r));
  if (sample.length === 0) return null;
  const columns =
    meta.columns && meta.columns.length > 0
      ? meta.columns
      : (sample[0] ?? []).map((_, i) => String(i + 1));
  const cell = (value: unknown): string =>
    value === null || value === undefined ? '' : String(value);
  const rows = sample.map((r) => columns.map((_, i) => cell(r[i])));
  return { columns: [...columns], rows };
}

/** Thresholds for `relativeTime`, largest first. */
const AGE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600_000],
  ['month', 30 * 24 * 3600_000],
  ['day', 24 * 3600_000],
  ['hour', 3600_000],
  ['minute', 60_000],
];

/**
 * "3 hours ago" / « il y a 3 heures » — the queue's *submitted* column.
 *
 * `Intl.RelativeTimeFormat` rather than a pair of translated strings per unit: the ten keys
 * plural rules would need in French are exactly what the platform already knows, and a
 * hand-written "il y a 1 heures" is the failure that comes of not using it.
 */
export function relativeTime(iso: string | null, locale: string, now = Date.now()): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const delta = then - now;
  const fmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, ms] of AGE_STEPS) {
    if (Math.abs(delta) >= ms) return fmt.format(Math.round(delta / ms), unit);
  }
  return fmt.format(0, 'minute');
}
